/**
 * Core trajectory: projectile motion + drag + gravity + crosswind.
 *
 * Solves a 2D (downrange × vertical) trajectory with drag using a fixed-step
 * forward-Euler integrator at 1ms steps. Fast enough for live recompute on
 * slider drag, accurate enough for archery distances.
 *
 * Anchored against the v140 calibration set in docs/field-data/.
 */

import type { Build, Environment, Distance, TrajectoryResult, GateResult } from './types';
import { airDensityForEnvironment } from './airDensity';
import { dragCoefficient } from './drag';
import { crosswindDrift, crosswindInducedYaw } from './crosswind';
import { computeLaunchVelocityFps } from './launchVelocity';

const GRAVITY_FT_S2 = 32.174;
const STEP_MS = 1;
const MAX_STEPS = 5000;
/* 1 slug = 32.174 lbm; 1 lbm = 7000 grains; so 1 slug = 225,218 grains */
const GRAINS_PER_SLUG = 7000 * 32.174;
const SHAFT_DIAMETER_REF_IN = 0.246;

interface FlightSample {
  tMs: number;
  xFt: number;
  yFt: number;
  vFps: number;
}

export function solveTrajectory(
  build: Build,
  env: Environment,
  dist: Distance
): TrajectoryResult {
  const launchFps = computeLaunchVelocityFps(build);
  const cd = dragCoefficient(build.vaneGeometry, launchFps, build.vaneOffsetDeg);
  const density = airDensityForEnvironment(env);
  const massSlug = build.arrowGrains / GRAINS_PER_SLUG;
  const areaFt2 = Math.PI * Math.pow(SHAFT_DIAMETER_REF_IN / 2 / 12, 2);

  const samples = integrateFlight({
    launchFps,
    shotElevationDeg: env.shotElevationDeg,
    targetXFt: dist.rangeYards * 3,
    cd,
    density,
    massSlug,
    areaFt2,
  });

  const final = interpolateAtRange(samples, dist.rangeYards * 3);

  /* Apex calculation — find peak height above the line from shooter to target. */
  const apex = findApex(samples, dist.rangeYards * 3, final.yFt);

  const driftIn = crosswindDrift({
    windFps: env.windMph * 1.467,
    windAngleDeg: env.windAngleDeg,
    tofSec: final.tMs / 1000,
    rangeFt: dist.rangeYards * 3,
    launchFps,
  });

  const inducedYaw = crosswindInducedYaw({
    windFps: env.windMph * 1.467,
    windAngleDeg: env.windAngleDeg,
    massGrains: build.arrowGrains,
    launchFps,
  });

  const launchKe = 0.5 * massSlug * launchFps * launchFps;
  const impactKe = 0.5 * massSlug * final.vFps * final.vFps;

  return {
    dropIn: -final.yFt * 12,
    driftIn,
    keFtLb: launchKe,
    keAtImpactFtLb: impactKe,
    retainedKePct: impactKe / launchKe,
    momentumSlugFtPerSec: massSlug * launchFps,
    momentumAtImpactSlugFtPerSec: massSlug * final.vFps,
    tofMs: final.tMs,
    impactYawDeg: build.tuneQualityYawDeg + inducedYaw,
    apexHeightIn: apex.heightIn,
    apexRangeYards: apex.rangeYards,
    gateResults: dist.gates?.map((g) =>
      gateResultAtRange(samples, g, build, env, massSlug, launchFps)
    ),
  };
}

/**
 * Find the apex (highest point) of the trajectory relative to the line
 * from shooter (0, 0) to target (targetXFt, finalYFt).
 *
 * The "apex above sight line" is the maximum perpendicular distance from
 * each trajectory sample to the straight line of sight. This is the
 * relevant measure for brush / branch clearance — it tells you how high
 * the arrow rises above the straight aim line.
 */
function findApex(
  samples: FlightSample[],
  targetXFt: number,
  finalYFt: number
): { heightIn: number; rangeYards: number } {
  if (samples.length === 0) return { heightIn: 0, rangeYards: 0 };

  /* Slope of the line from origin (0,0) to (targetXFt, finalYFt) */
  const slope = targetXFt > 0 ? finalYFt / targetXFt : 0;

  let maxHeightFt = 0;
  let maxAtXFt = 0;

  for (const sample of samples) {
    if (sample.xFt > targetXFt) break;
    const lineY = slope * sample.xFt;
    const heightAbove = sample.yFt - lineY;
    if (heightAbove > maxHeightFt) {
      maxHeightFt = heightAbove;
      maxAtXFt = sample.xFt;
    }
  }

  return {
    heightIn: maxHeightFt * 12,
    rangeYards: maxAtXFt / 3,
  };
}

interface IntegrateInputs {
  launchFps: number;
  shotElevationDeg: number;
  targetXFt: number;
  cd: number;
  density: number;
  massSlug: number;
  areaFt2: number;
}

function integrateFlight(inputs: IntegrateInputs): FlightSample[] {
  const samples: FlightSample[] = [];
  const elevRad = (inputs.shotElevationDeg * Math.PI) / 180;
  let tMs = 0;
  let xFt = 0;
  let yFt = 0;
  let vxFps = inputs.launchFps * Math.cos(elevRad);
  let vyFps = inputs.launchFps * Math.sin(elevRad);

  while (xFt < inputs.targetXFt && tMs < MAX_STEPS * STEP_MS) {
    const dt = STEP_MS / 1000;
    const vFps = Math.hypot(vxFps, vyFps);
    const dragForce = 0.5 * inputs.density * vFps * vFps * inputs.cd * inputs.areaFt2;
    const aDrag = dragForce / inputs.massSlug;
    const angleRad = Math.atan2(vyFps, vxFps);

    const aDragX = -aDrag * Math.cos(angleRad);
    const aDragY = -aDrag * Math.sin(angleRad) - GRAVITY_FT_S2;

    vxFps += aDragX * dt;
    vyFps += aDragY * dt;
    xFt += vxFps * dt;
    yFt += vyFps * dt;
    tMs += STEP_MS;

    samples.push({ tMs, xFt, yFt, vFps });
  }
  return samples;
}

function interpolateAtRange(samples: FlightSample[], targetXFt: number): FlightSample {
  /* No flight was integrated (e.g. a zero/negative target distance). Return a
   * defined zero sample so callers never dereference `undefined` — a non-
   * physical request must degrade to zeros, never throw and white-screen. */
  if (samples.length === 0) {
    return { tMs: 0, xFt: targetXFt, yFt: 0, vFps: 0 };
  }
  for (let i = 1; i < samples.length; i++) {
    if (samples[i].xFt >= targetXFt) {
      const a = samples[i - 1];
      const b = samples[i];
      const t = (targetXFt - a.xFt) / (b.xFt - a.xFt);
      return {
        tMs: a.tMs + t * (b.tMs - a.tMs),
        xFt: targetXFt,
        yFt: a.yFt + t * (b.yFt - a.yFt),
        vFps: a.vFps + t * (b.vFps - a.vFps),
      };
    }
  }
  return samples[samples.length - 1];
}

function gateResultAtRange(
  samples: FlightSample[],
  gateYards: number,
  build: Build,
  env: Environment,
  massSlug: number,
  launchFps: number
): GateResult {
  const final = interpolateAtRange(samples, gateYards * 3);
  const ke = 0.5 * massSlug * final.vFps * final.vFps;
  const drift = crosswindDrift({
    windFps: env.windMph * 1.467,
    windAngleDeg: env.windAngleDeg,
    tofSec: final.tMs / 1000,
    rangeFt: gateYards * 3,
    launchFps,
  });
  return {
    rangeYards: gateYards,
    dropIn: -final.yFt * 12,
    driftIn: drift,
    keFtLb: ke,
    tofMs: final.tMs,
    impactYawDeg:
      build.tuneQualityYawDeg +
      crosswindInducedYaw({
        windFps: env.windMph * 1.467,
        windAngleDeg: env.windAngleDeg,
        massGrains: build.arrowGrains,
        launchFps,
      }),
  };
}
